import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Ticket as TicketIcon, Funnel, RefreshCw, ScrollText } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import {
  ContentCard,
  EmptyState,
  LoadingState,
  PageHeader,
  ui } from
"../../../components/ui";
import { cn } from "../../../lib/utils";
import { hasCanonicalRole } from "../../../lib/auth";
import sessionDB from "../../../utils/sessionDB";
import {
  TICKET_STATUS_CLASS,
  isClosedTicketStatus,
  translateTicketStatus,
  translateTicketPriority,
  getPriorityClass } from
"../data/ticketConstants";
import {
  countActiveTicketFilters,
  getDefaultTicketFilters,
  resolveTicketListFilter } from
"../data/ticketFilters";
import { TicketFiltersModal } from "../components/TicketFiltersModal";
import { ComplaintLogsModal } from "../components/ComplaintLogsModal";
import {
  searchCustomerComplaints,
  searchComplaintsByTechnician,
  searchComplaintsByCustomer,
  searchComplaintsByStatus } from
"../../../services/fsmService";
import { PageBackLink } from '../../../components/layout/PageBackLink';

/* ─── Permission scaffold ──────────────────────────────────────────────── */
const PAGE_SIZE = 10;

const TABLE_COLUMNS = [
{ key: "complaintNo", label: "Complaint", className: "whitespace-nowrap" },
{ key: "customer", label: "Customer", className: "" },
{ key: "contactNumber", label: "Phone", className: "whitespace-nowrap" },
{ key: "system", label: "System", className: "whitespace-nowrap" },
{ key: "priority", label: "Priority", className: "whitespace-nowrap" },
{ key: "status", label: "Status", className: "whitespace-nowrap" },
{ key: "date", label: "Date", className: "whitespace-nowrap" },
{ key: "action", label: "Action", className: "whitespace-nowrap text-right w-[96px]" }];


const pagerBtn =
"h-8 w-8 grid place-items-center rounded-lg border border-[var(--color-text-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/40 hover:text-[var(--color-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

function formatDate(iso) {
  if (!iso) return "—";
  const datePart = String(iso).slice(0, 10);
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function TicketStatusBadge({ status }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded-full text-[11px] font-semibold",
        TICKET_STATUS_CLASS[status] ||
        "bg-[var(--color-text-secondary)]/12 text-[var(--color-text-secondary)]"
      )}>
      
      <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-current" />
      <span className="truncate">
        {translateTicketStatus(status, i18n.t.bind(i18n))}
      </span>
    </span>);

}

function PriorityLabel({ priority }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-[13px] font-semibold",
        getPriorityClass(priority)
      )}>
      
      <span className="h-2 w-2 rounded-full shrink-0 bg-current" />
      {translateTicketPriority(priority, i18n.t.bind(i18n)) || "—"}
    </span>);

}

function TicketsPagination({ page, totalPages, totalItems, pageSize, onPageChange }) {
  if (totalItems <= 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-[var(--color-text-border)]/60 text-xs text-[var(--color-text-secondary)]">
      <span>
        <Trans i18nKey="pagination.showing" /> {start}-{end} <Trans i18nKey="common.of" /> {totalItems}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          aria-label={i18n.t("pagination.previousPage")}
          className={pagerBtn}>
          
          ‹
        </button>
        <span className="px-2 text-xs font-semibold text-foreground">
          {page} / {Math.max(1, totalPages)}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          aria-label={i18n.t("pagination.nextPage")}
          className={pagerBtn}>
          
          ›
        </button>
      </div>
    </div>);

}

/* eslint-disable-next-line no-unused-vars -- retained for the planned closed-ticket control */
function ClosedTicketsToggle({ checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
      <span
        className={cn(
          "relative inline-flex h-5 w-9 rounded-full transition-colors duration-200",
          checked ? "bg-[var(--color-primary)]" : "bg-[var(--color-text-border)]"
        )}
        onClick={onChange}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") onChange();
        }}>
        
        <span
          className={cn(
            "absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200",
            checked ? "translate-x-4" : "translate-x-0"
          )} />
        
      </span>
      <span className="text-sm font-semibold text-[var(--color-text-secondary)]">
        <Trans i18nKey="fsm.showClosedTickets" />
      </span>
    </label>);

}

function TicketActionButtons({ ticket, onSelect, onViewLogs }) {
  return (
    <div
      className="inline-flex items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}>
      
      {/* <button
        type="button"
        title={i18n.t("fsm.review")}
        aria-label={i18n.t("fsm.review")}
        onClick={() => onSelect(ticket)}
        className="inline-grid place-items-center h-8 w-8 rounded-lg border border-[var(--color-primary)]/30 text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white transition cursor-pointer">
        
        <Eye className="h-4 w-4" />
      </button> */}
      <button
        type="button"
        title={i18n.t("fsm.viewLogs")}
        aria-label={i18n.t("fsm.viewLogs")}
        onClick={() => onViewLogs(ticket)}
        className="inline-grid place-items-center h-8 w-8 rounded-lg border border-[var(--color-primary)]/30 text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white transition cursor-pointer">
        
        <ScrollText className="h-4 w-4" />
      </button>
    </div>);

}

function TicketTableRow({ ticket, onSelect, onViewLogs }) {
  return (
    <tr
      className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer"
      onClick={() => onSelect(ticket)}>
      
      <td className="px-3 py-3.5 whitespace-nowrap font-semibold text-[var(--color-primary)]">
        {ticket.complaintNo}
      </td>
      <td className="px-3 py-3.5">
        <p className="font-semibold text-foreground truncate">{ticket.customerName}</p>
        {ticket.customerSub ?
        <p className="text-xs text-[var(--color-text-secondary)] truncate">
            {ticket.customerSub}
          </p> :
        null}
      </td>
      <td className="px-3 py-3.5 whitespace-nowrap text-[var(--color-text-secondary)]">
        {ticket.contactNumber || "—"}
      </td>
      <td className="px-3 py-3.5 text-[var(--color-text-secondary)]">
        <p className="truncate" title={ticket.system}>{ticket.system}</p>
      </td>
      <td className="px-2 py-3.5 whitespace-nowrap">
        <PriorityLabel priority={ticket.priority} />
      </td>
      <td className="px-2 py-3.5">
        <TicketStatusBadge status={ticket.status} />
      </td>
      <td className="px-3 py-3.5 whitespace-nowrap text-[var(--color-text-secondary)]">
        {formatDate(ticket.createdAt)}
      </td>
      <td className="px-2 py-3.5 whitespace-nowrap text-right w-[96px]">
        <TicketActionButtons ticket={ticket} onSelect={onSelect} onViewLogs={onViewLogs} />
      </td>
    </tr>);

}

function TicketMobileCard({ ticket, onSelect, onViewLogs }) {
  return (
    <div className="w-full bg-white rounded-xl border border-[var(--color-text-border)]/60 px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="min-w-0 text-left cursor-pointer"
          onClick={() => onSelect(ticket)}>
          
          <span className="font-semibold text-[var(--color-primary)]">{ticket.complaintNo}</span>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <TicketStatusBadge status={ticket.status} />
          <TicketActionButtons ticket={ticket} onSelect={onSelect} onViewLogs={onViewLogs} />
        </div>
      </div>
      <button
        type="button"
        className="w-full text-left cursor-pointer mt-1"
        onClick={() => onSelect(ticket)}>
        
        <p className="font-semibold text-foreground leading-snug">{ticket.customerName}</p>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p className="text-xs text-[var(--color-text-secondary)] truncate min-w-0">
            {ticket.customerSub || " "}
          </p>
          <span className="text-xs text-[var(--color-text-secondary)] shrink-0 whitespace-nowrap">
            {formatDate(ticket.createdAt)}
          </span>
        </div>
        {ticket.contactNumber ?
        <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">
            {ticket.contactNumber}
          </p> :
        null}
        <div className="flex items-center gap-2 flex-wrap mt-1.5">
          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[var(--color-workspace)]/60 text-[var(--color-primary)] text-xs font-semibold">
            {ticket.system}
          </span>
          <PriorityLabel priority={ticket.priority} />
        </div>
      </button>
    </div>);

}

/**
 * FsmTicketsPage — FSM customer complaints list.
 * Managers: search + customer / technician / status filter endpoints
 * Technicians: own tickets + customer / status filters (scoped to signed-in tech)
 */
export function FsmTicketsPage() {
  const navigate = useNavigate();
  const isTechnician = hasCanonicalRole("technician");
  const canCreateTicket = !isTechnician;
  const canFilterTickets = true;

  const [tickets, setTickets] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showClosed] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(getDefaultTicketFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [logsTicket, setLogsTicket] = useState(null);

  const activeFilterCount = countActiveTicketFilters(filters);
  const activeListFilter = resolveTicketListFilter(filters);

  const loadTickets = useCallback(async (pageIndex, { signal } = {}) => {
    setLoading(true);
    setError("");
    try {
      const paging = {
        page: pageIndex,
        size: PAGE_SIZE,
        sortBy: "complaintId",
        sortDir: "DESC"
      };

      let result;
      if (isTechnician) {
        const empId = sessionDB.getEmpId();
        const active = resolveTicketListFilter(filters);
        if (active.type === "status") {
          result = await searchComplaintsByStatus(active.value, paging);
          const ownItems = (result.items || []).filter(
            (t) => String(t.technicianId ?? t.raw?.technicianId ?? "") === String(empId)
          );
          result = { ...result, items: ownItems, total: ownItems.length };
        } else if (active.type === "customer") {
          result = await searchComplaintsByCustomer(active.value, paging);
          const ownItems = (result.items || []).filter(
            (t) => String(t.technicianId ?? t.raw?.technicianId ?? "") === String(empId)
          );
          result = { ...result, items: ownItems, total: ownItems.length };
        } else {
          // Always scope to signed-in technician (ignore technician filter dimension)
          result = await searchComplaintsByTechnician(empId, paging);
        }
      } else {
        const active = resolveTicketListFilter(filters);
        if (active.type === "status") {
          result = await searchComplaintsByStatus(active.value, paging);
        } else if (active.type === "technician") {
          result = await searchComplaintsByTechnician(active.value, paging);
        } else if (active.type === "customer") {
          result = await searchComplaintsByCustomer(active.value, paging);
        } else {
          result = await searchCustomerComplaints(paging);
        }
      }

      if (signal?.aborted) return;
      setTickets(result.items);
      setTotalItems(result.total);
    } catch (err) {
      if (signal?.aborted) return;
      console.error("Failed to load tickets:", err);
      setTickets([]);
      setTotalItems(0);
      setError(
        err.status === 401 ?
        "Session expired. Please refresh and try again." :
        err.message || "Failed to load tickets."
      );
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [isTechnician, filters]);

  // Defer fetch so React Strict Mode's immediate remount cancels the first
  // scheduled call — avoids duplicate list API requests on navigate/mount.
  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (!controller.signal.aborted) {
        loadTickets(page - 1, { signal: controller.signal });
      }
    }, 0);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [page, loadTickets]);

  const visibleTickets = useMemo(() => {
    // Server status/customer/technician filters already shape the result set
    if (activeListFilter.type !== "all") return tickets;
    if (showClosed) return tickets;
    return tickets.filter(
      (t) => !isClosedTicketStatus(t.status) && !isClosedTicketStatus(t.statusRaw)
    );
  }, [tickets, showClosed, activeListFilter.type]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleApplyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters(getDefaultTicketFilters());
    setPage(1);
  };

  const handleSelectTicket = (ticket) => {
    navigate(`/fsm/customer-complaints/${ticket.id}`);
  };

  const handleViewLogs = (ticket) => {
    setLogsTicket(ticket);
  };

  const handleCreateTicket = () => {
    navigate("/fsm/create-customer-complaint");
  };

  return (
    <AppLayout>
      {canFilterTickets ?
      <TicketFiltersModal
        open={filterModalOpen}
        onOpenChange={setFilterModalOpen}
        appliedFilters={filters}
        onApply={handleApplyFilters}
        hideTechnicianFilter={isTechnician} /> :

      null}

      <ComplaintLogsModal
        open={Boolean(logsTicket)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setLogsTicket(null);
        }}
        ticket={logsTicket} />
      <div className="sticky top-[-24px] lg:top-[-28px] z-20 -mt-2 mb-6 bg-[var(--color-workspace)] pt-2 pb-3">
        <PageBackLink to="/" />
        <PageHeader
          title={i18n.t("fsm.tickets")}
          // subtitle={
          // isTechnician ?
          // "Your assigned complaints, newest first." :
          // "All complaints, newest first."
          // }
          className="mb-0"
          action={
          canCreateTicket ?
          <button
            type="button"
            onClick={handleCreateTicket}
            className={ui.primaryBtn}>
            
                <Plus className="h-4 w-4" />
                <Trans i18nKey="formActions.createTicket" />
              </button> :
          null
          } />
      </div>

      {/* {activeListFilter.type === "all" ?
      <div className="mb-4">
          <ClosedTicketsToggle
          checked={showClosed}
          onChange={handleToggleClosed} />
        
        </div> :
      null} */}

      <ContentCard>
        {canFilterTickets ?
        <div className="px-4 sm:px-6 py-3 border-b border-[var(--color-text-border)]/70 flex flex-wrap items-center justify-end gap-1.5">
            <button
            type="button"
            onClick={() => setFilterModalOpen(true)}
            className={ui.filterBtn}>
            
              <Funnel className="h-4 w-4 shrink-0" />
              <span><Trans i18nKey="formActions.filter" /></span>
              {activeFilterCount > 0 &&
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[11px] font-bold text-white">
                  {activeFilterCount}
                </span>
            }
            </button>

            <button
            type="button"
            onClick={handleResetFilters}
            disabled={activeFilterCount === 0}
            className={`${ui.filterBtn} disabled:opacity-50 disabled:cursor-not-allowed`}>
            
              <RefreshCw className="h-4 w-4 shrink-0" />
              <span><Trans i18nKey="formActions.resetFilters" /></span>
            </button>
          </div> :
        null}

        {loading ?
        <LoadingState message="Loading tickets..." /> :
        error ?
        <EmptyState
          icon={TicketIcon}
          title={i18n.t("validation.unableToLoadTickets")}
          message={error} /> :

        visibleTickets.length === 0 ?
        <EmptyState
          icon={TicketIcon}
          title={
          activeFilterCount > 0 ?
          "No tickets found." :
          showClosed ?
          "No tickets found." :
          "No open tickets."
          }
          message={
          activeFilterCount > 0 ?
          "Try adjusting your filters to see more results." :
          showClosed ?
          "There are no tickets matching the current filters." :
          isTechnician ?
          "No tickets assigned to you yet." :
          "All caught up! Create a new ticket to get started."
          } /> :


        <>
            <div className="hidden md:block w-full">
              <table className="w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[8%]" />
                  <col className="w-[14%]" />
                  <col className="w-[11%]" />
                  <col className="w-[22%]" />
                  <col className="w-[8%]" />
                  <col className="w-[16%]" />
                  <col className="w-[10%]" />
                  <col className="w-[96px]" />
                </colgroup>
                <thead>
                  <tr className={ui.tableHead}>
                    {TABLE_COLUMNS.map((col) =>
                  <th
                    key={col.key}
                    className={cn(
                      "py-3 font-semibold",
                      col.key === "priority" || col.key === "status" || col.key === "action" ?
                      "px-2" :
                      "px-3",
                      col.className
                    )}>
                    
                        {col.key === "phone" ? i18n.t("common.phone") :
                      col.key === "priority" ? i18n.t("fsm.priority") :
                      col.label}
                      </th>
                  )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-text-border)]/35">
                  {visibleTickets.map((ticket) =>
                <TicketTableRow
                  key={ticket.id}
                  ticket={ticket}
                  onSelect={handleSelectTicket}
                  onViewLogs={handleViewLogs} />

                )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden flex flex-col gap-2 px-3 py-3">
              {visibleTickets.map((ticket) =>
            <TicketMobileCard
              key={ticket.id}
              ticket={ticket}
              onSelect={handleSelectTicket}
              onViewLogs={handleViewLogs} />

            )}
            </div>

            <TicketsPagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={PAGE_SIZE}
            onPageChange={setPage} />
          
          </>
        }
      </ContentCard>
    </AppLayout>);

}
